import { useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, Minus, Plus } from 'lucide-react';

export const SIZE_PICKER_MAX_QUANTITY = 99;
/** Sizes named in the closed field before the rest is counted. */
export const SIZE_SUMMARY_VISIBLE = 3;

interface SizeOption {
  id: string;
  name: string;
}

export interface SizePickerNotice {
  kind: 'required' | 'removed';
  /** Already translated by the parent. */
  text: string;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * What the closed field says: "2 × M, 1 × L", from the fourth size on
 * "2 × S, 1 × M, 1 × L +2". Returns null when nothing is chosen.
 *
 * Walks `sizes`, not `quantities`: the order is the one the customer sees
 * in the list, and a quantity left over for a size the colour does not
 * offer never reaches the field.
 */
export function summarizeSizes(
  sizes: SizeOption[],
  quantities: Record<string, number>,
  t: Translate
): string | null {
  const chosen = sizes.filter((size) => (quantities[size.id] ?? 0) > 0);
  if (chosen.length === 0) return null;

  const named = chosen
    .slice(0, SIZE_SUMMARY_VISIBLE)
    .map((size) =>
      t('directSale.desktop.sizeSummaryItem', {
        count: quantities[size.id],
        size: size.name,
      })
    )
    .join(', ');

  const further = chosen.length - SIZE_SUMMARY_VISIBLE;
  if (further <= 0) return named;
  return `${named} ${t('directSale.desktop.sizeSummaryMore', { count: further })}`;
}

interface SizeRowProps {
  size: SizeOption;
  count: number;
  onQuantityChange: (sizeId: string, quantity: number) => void;
}

/** One size with its stepper, after the buy sheet on the phone. */
function SizeRow({ size, count, onQuantityChange }: SizeRowProps) {
  const { t } = useTranslation();

  return (
    <>
      <span className="text-base font-bold text-ink">{size.name}</span>
      <div
        role="group"
        aria-label={size.name}
        className="flex h-10 items-stretch overflow-hidden rounded-control border border-line"
      >
        <button
          type="button"
          aria-label={t('directSale.mobile.fewer')}
          disabled={count <= 0}
          onClick={() => onQuantityChange(size.id, count - 1)}
          className="flex w-10 items-center justify-center text-ink focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary disabled:text-ink-3"
        >
          <Minus className="h-4 w-4" strokeWidth={2.2} />
        </button>
        <output
          aria-live="polite"
          className="flex w-10 items-center justify-center border-x border-line text-base font-semibold text-ink"
        >
          {count}
        </output>
        <button
          type="button"
          aria-label={t('directSale.mobile.more')}
          disabled={count >= SIZE_PICKER_MAX_QUANTITY}
          onClick={() => onQuantityChange(size.id, count + 1)}
          className="flex w-10 items-center justify-center text-ink focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary disabled:text-ink-3"
        >
          <Plus className="h-4 w-4" strokeWidth={2.2} />
        </button>
      </div>
    </>
  );
}

interface DirectSaleSizePickerProps {
  sizes: SizeOption[];
  quantities: Record<string, number>;
  onQuantityChange: (sizeId: string, quantity: number) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  notice?: SizePickerNotice | null;
}

/**
 * Size field of the purchase column, after Spreadshirt: closed it sums up
 * the choice, open it lists one stepper per size.
 *
 * Fully controlled. The purchase column owns the quantities and the open
 * flag because it has to open the list itself (cart click without a size)
 * and decides in which order Escape closes things — so Escape is not
 * handled here.
 */
export function DirectSaleSizePicker({
  sizes,
  quantities,
  onQuantityChange,
  open,
  onOpenChange,
  notice,
}: DirectSaleSizePickerProps) {
  const { t } = useTranslation();
  const listId = useId();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // Remembers the last state the focus was moved for. Starts at "closed"
  // so that mounting closed leaves the focus alone, while mounting open
  // (cart click without a size) still leads into the list.
  const focusedForOpen = useRef(false);

  const hasList = sizes.length > 1;
  const listOpen = hasList && open;

  useEffect(() => {
    if (focusedForOpen.current === listOpen) return;
    focusedForOpen.current = listOpen;
    if (listOpen) {
      // Minus is disabled at zero, so the first stop is usually plus.
      listRef.current
        ?.querySelector<HTMLButtonElement>('button:not(:disabled)')
        ?.focus();
    } else {
      fieldRef.current?.focus();
    }
  }, [listOpen]);

  useEffect(() => {
    if (!listOpen) return;
    // mousedown, not click: the list is gone before the element under the
    // pointer reacts, and that element still receives its own click.
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Node && wrapperRef.current?.contains(target)) {
        return;
      }
      onOpenChange(false);
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [listOpen, onOpenChange]);

  if (sizes.length === 0) {
    return (
      <p className="text-sm text-ink-2">
        {t('directSale.desktop.unavailable')}
      </p>
    );
  }

  const noticeLine = notice && (
    <p
      role="alert"
      className={`mt-2 text-sm ${
        notice.kind === 'required' ? 'text-danger' : 'text-ink-2'
      }`}
    >
      {notice.text}
    </p>
  );

  const [only] = sizes;
  if (!hasList && only) {
    // Nothing to choose between: the stepper stands where the field would.
    return (
      <div>
        <div className="flex h-12 items-center justify-between rounded-control border border-line pl-4 pr-1">
          <SizeRow
            size={only}
            count={quantities[only.id] ?? 0}
            onQuantityChange={onQuantityChange}
          />
        </div>
        {noticeLine}
      </div>
    );
  }

  const summary = summarizeSizes(sizes, quantities, (key, options) =>
    t(key, options)
  );
  const Chevron = open ? ChevronUp : ChevronDown;

  return (
    <div ref={wrapperRef} className="relative">
      {open && (
        // Opens upwards: price and cart button sit right below the field
        // and must stay visible while the customer counts.
        <div
          ref={listRef}
          id={listId}
          className="absolute bottom-full left-0 right-0 z-20 mb-1 max-h-[60vh] overflow-y-auto rounded-card border border-line bg-paper"
        >
          {sizes.map((size, index) => (
            <div
              key={size.id}
              className={`flex h-14 items-center justify-between px-4 ${
                index > 0 ? 'border-t border-line' : ''
              }`}
            >
              <SizeRow
                size={size}
                count={quantities[size.id] ?? 0}
                onQuantityChange={onQuantityChange}
              />
            </div>
          ))}
        </div>
      )}

      <button
        ref={fieldRef}
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => onOpenChange(!open)}
        className="flex h-12 w-full items-center justify-between gap-3 rounded-control border border-line bg-paper px-4 text-left text-base text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
      >
        <span className={`truncate ${summary ? 'font-semibold' : ''}`}>
          {summary ?? t('directSale.mobile.pickSizeCta')}
        </span>
        <Chevron className="h-5 w-5 flex-none text-ink-2" />
      </button>

      {noticeLine}
    </div>
  );
}
